import { cloneDeep } from "lodash";
import styles from "./css/PlaceHolderTextField.module.css";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { toDecimalString } from "./strings";

interface PlaceHolderTextFieldProps {
  text?: string;
  hint?: string;
  minCount?: number;
  maxCount?: number;
  type?: string;
  lineStyle: "single" | "multiple";
  backgroundColor?: string;
  showingBorder?: boolean;
  onGlobalFocus?: boolean;
  onChangeFocus?: (focus: boolean) => void;
  handleTextChange?: (text: string) => void;
  onKeyDownInput?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyDownTextArea?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  inputRef?: (el: HTMLInputElement | null) => void;
  textAreaRef?: React.Ref<HTMLTextAreaElement>;
  autoFocus?: boolean;
}

function PlaceHolderTextField(props: PlaceHolderTextFieldProps) {
  const { t } = useTranslation();

  const internalTextareaRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [textToSave, setTextToSave] = useState<string>(
    cloneDeep(props.text || "")
  );

  const [onFocus, setOnFocus] = useState(false);
  // 한 번이라도 입력칸을 벗어난 뒤에만 오류를 보여준다 (열자마자 오류가 뜨지 않도록)
  const [touched, setTouched] = useState(false);

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTextToSave(e.target.value);
    if (props.handleTextChange !== undefined) {
      props.handleTextChange(e.target.value);
    }
  };

  const handleValueChangeForMultiple = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setTextToSave(e.target.value);
    if (props.handleTextChange !== undefined) {
      props.handleTextChange(e.target.value);
    }
  };

  const containerClass = classNames(styles.placeHolderTextField, {
    [styles.placeHolderTextField]: props.showingBorder === undefined,
    [styles.placeHolderTextFieldWithBorder]:
      props.showingBorder !== undefined && props.showingBorder === true,
  });

  const maxCountClass = classNames(styles.maxCountText, {
    [styles.maxCountNormal]: (props.maxCount ?? 0) >= textToSave.length,
    [styles.maxCountOver]: (props.maxCount ?? 0) < textToSave.length,
  });

  useEffect(() => {
    setTextToSave(cloneDeep(props.text || ""));
  }, [props.text]);

  useEffect(() => {
    if (props.type === "number" && props.maxCount !== undefined) {
      const maxCount = +props.maxCount;
      const value = parseInt(textToSave, 10);
      if (value > maxCount) {
        setTextToSave(maxCount.toString());
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textToSave]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [textToSave]);

  useEffect(() => {
    const textarea = internalTextareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [textToSave]);

  useEffect(() => {
    if (props.textAreaRef) {
      if (typeof props.textAreaRef === "function") {
        props.textAreaRef(internalTextareaRef.current);
      } else {
        (
          props.textAreaRef as React.MutableRefObject<HTMLTextAreaElement | null>
        ).current = internalTextareaRef.current;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.textAreaRef, internalTextareaRef.current]);

  useEffect(() => {
    if (props.onChangeFocus !== undefined) {
      props.onChangeFocus(onFocus);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onFocus]);

  return (
    <>
      <div className={styles.container}>
        <div
          className={containerClass}
          style={{
            backgroundColor:
              props.backgroundColor === null
                ? "var(--button_color)"
                : props.backgroundColor,
          }}
        >
          {props.lineStyle === "single" ? (
            <input
              autoFocus={props.autoFocus}
              className={styles.input}
              value={textToSave}
              placeholder={props.hint}
              type={props.type}
              step={5}
              onChange={handleValueChange}
              onKeyDown={props.onKeyDownInput}
              ref={props.inputRef}
              onFocus={() => {
                setOnFocus(true);
              }}
              onBlur={() => {
                setOnFocus(false);
                setTouched(true);
              }}
            />
          ) : (
            <textarea
              className={styles.textarea}
              value={textToSave}
              placeholder={props.hint}
              onChange={handleValueChangeForMultiple}
              rows={1}
              onKeyDown={props.onKeyDownTextArea}
              ref={internalTextareaRef}
              onFocus={() => {
                setOnFocus(true);
              }}
              onBlur={() => {
                setOnFocus(false);
                setTouched(true);
              }}
            />
          )}
          {props.maxCount !== undefined &&
            onFocus === true &&
            props.type !== "number" && (
              <p className={maxCountClass}>{`${toDecimalString(
                textToSave.length
              )}/${toDecimalString(props.maxCount)}`}</p>
            )}
        </div>
        {props.maxCount !== undefined &&
          textToSave.length > props.maxCount &&
          props.onGlobalFocus === false &&
          touched &&
          props.type !== "number" && (
            <span className={styles.errorMessageText}>
              {t("TextCountOverErrorMessage", { count: props.maxCount })}
            </span>
          )}
        {props.minCount !== undefined &&
          textToSave.length < props.minCount &&
          props.onGlobalFocus === false &&
          touched &&
          props.type !== "number" && (
            <span className={styles.errorMessageText}>
              {t("TextCountLessThanOneMessage")}
            </span>
          )}
      </div>
    </>
  );
}

export default PlaceHolderTextField;
